import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import WalletButton from '../WalletButton/WalletButton'
import styles from './Navbar.module.css'

const navLinks = [
  { href: '#features', label: 'Features' },
  { href: '#architecture', label: 'Architecture' },
  { href: '#quickstart', label: 'Quickstart' },
  { href: '#sdks', label: 'SDKs' },
  { href: 'https://github.com/luminox-mem/luminox#readme', label: 'Docs', external: true },
]

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault()
      const target = document.querySelector(href)
      if (target) {
        const offset = 100
        const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - offset
        window.scrollTo({ top: targetPosition, behavior: 'smooth' })
      }
      setMobileMenuOpen(false)
    }
  }

  return (
    <motion.nav
      className={`${styles.navbar} ${scrolled ? styles.scrolled : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className={styles.container}>
        <motion.a
          href="#"
          className={styles.logo}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.span
            className={styles.logoIcon}
            animate={{
              textShadow: [
                '0 0 20px rgba(255, 215, 0, 0.4)',
                '0 0 50px rgba(255, 215, 0, 0.8)',
                '0 0 20px rgba(255, 215, 0, 0.4)',
              ],
              rotate: [0, 5, 0, -5, 0],
              scale: [1, 1.1, 1, 1.1, 1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            &#9672;
          </motion.span>
          <motion.span
            className={styles.logoText}
            animate={{
              textShadow: [
                '0 0 10px rgba(255, 215, 0, 0)',
                '0 0 20px rgba(255, 215, 0, 0.5)',
                '0 0 30px rgba(255, 215, 0, 0.3)',
                '0 0 10px rgba(255, 215, 0, 0)',
              ],
              y: [0, -2, 0, 2, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            LUMINOX
          </motion.span>
        </motion.a>

        <div className={`${styles.navLinks} ${mobileMenuOpen ? styles.open : ''}`}>
          {navLinks.map((link, index) => (
            <motion.a
              key={link.href}
              href={link.href}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener noreferrer' : undefined}
              onClick={(e) => handleSmoothScroll(e, link.href)}
              className={styles.navLink}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index, duration: 0.4 }}
              whileHover={{ color: '#FFD700' }}
            >
              {link.label}
            </motion.a>
          ))}
        </div>

        <motion.a
          href="https://github.com/luminox-mem/luminox"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.githubBtn}
          whileHover={{ scale: 1.05, boxShadow: '0 8px 30px rgba(255, 215, 0, 0.4)' }}
          whileTap={{ scale: 0.95 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
          </svg>
          GitHub
        </motion.a>

        <WalletButton />

        <button
          className={`${styles.mobileToggle} ${mobileMenuOpen ? styles.open : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </motion.nav>
  )
}
